<template>
    <modal
        :active="active"
        @closed="$emit('closed', true)"
        :title="title()"
        :large="true"
    >
        <div class="columns is-tablet">
            <form-field
                class="column"
                label="Navn"
                :error="error.name"
            >
                <input-text
                    v-model="form.name"
                    :error="error.name"
                />
            </form-field>
            <form-field
                class="column"
                label="Oprindelsesland"
                :error="error.country_id"
            >
                <InputSelectSearch
                    :options="listCountries"
                    v-model="countryId"
                    :error="error.country_id"
                    label="title"
                />
            </form-field>
        </div>

        <div class="columns is-tablet">
            <form-field
                class="column"
                label="Hovednummer"
                :error="error.phone"
            >
                <input-text
                    v-model="form.phone"
                    :error="error.phone"
                />
            </form-field>

            <form-field
                class="column"
                label="Adresse"
                :error="error.address"
            >
                <input-text
                    v-model="form.address"
                    :error="error.address"
                />
            </form-field>
        </div>

        <div class="columns is-tablet">
            <form-field
                class="column"
                label="Postnummer"
                :error="error.zipcode"
            >
                <input-text
                    v-model="form.zipcode"
                    :error="error.zipcode"
                />
            </form-field>

            <form-field
                class="column"
                label="By"
                :error="error.city"
            >
                <input-text
                    v-model="form.city"
                    :error="error.city"
                />
            </form-field>
        </div>

        <div class="columns is-tablet">
            <form-field
                class="column"
                label="E-mail"
                :error="error.email"
            >
                <input-text
                    v-model="form.email"
                    :error="error.email"
                />
            </form-field>
            <form-field
                class="column"
                label="Valuta"
                :error="error.currency"
                inner-container-class="select is-fullwidth"
            >
                <input-select
                    :options="currencies"
                    v-model="form.currency"
                    :error="error.currency"
                />
            </form-field>
        </div>

        <div
            v-if="economicIsEnabled"
            class="columns is-tablet"
        >
            <form-field
                class="column"
                label="Leverandørgruppe"
                :error="error.supplier_group_number"
                inner-container-class="select is-fullwidth"
            >
                <input-select
                    :options="economic.supplierGroups"
                    v-model="form.supplier_group_number"
                    :error="error.supplier_group_number"
                />
            </form-field>

            <form-field
                class="column"
                label="Betalingsbetingelser"
                :error="error.payment_terms_number"
                inner-container-class="select is-fullwidth"
            >
                <input-select
                    :options="economic.paymentTerms"
                    v-model="form.payment_terms_number"
                    :error="error.payment_terms_number"
                />
            </form-field>
        </div>

        <div
            v-if="economicIsEnabled"
            class="columns is-tablet"
        >
            <form-field
                class="column"
                label="Momszone"
                :error="error.vat_zone_number"
                inner-container-class="select is-fullwidth"
            >
                <input-select
                    :options="economic.vatZones"
                    v-model="form.vat_zone_number"
                    :error="error.vat_zone_number"
                />
            </form-field>

            <form-field
                v-if="economicIsEnabled && !isEdit"
                class="column"
                label="Leverandør nr."
                help-text="Udfyldes automatisk. Dobbeltklik på feltet for manuel redigering."
                :error="error.economic_id"
                @dblclick="useCustomEconomicId = true"
            >
                <input-text
                    :disabled="!useCustomEconomicId"
                    v-model="form.economic_id"
                    :error="error.economic_id"
                />
            </form-field>
        </div>

        <template v-slot:footer class="has-space-between">
            <div class="footer-spacer">
                <div>
                    <button class="button is-danger is-flex-left" @click="reset">Nulstil</button>
                    <button class="button is-default" @click="$emit('closed', true)">Annuller</button>
                    <button class="button is-primary" @click="submit">{{ saveButtonText }}</button>
                </div>

                <button class="button is-danger" @click="remove" v-if="isEdit">Slet</button>
            </div>
        </template>
    </modal>
</template>

<script>
import Modal from '@elems/modal'
import { FormField, InputText, InputCheckbox, InputSelect, InputDecimal } from '@elems/form'
import InputSelectSearch from "../../elements/form/InputSelectSearch";
import {mapGetters} from "vuex";

export default {
    components: {
        InputSelectSearch,
        Modal,
        FormField,
        InputText,
        InputSelect,
        InputCheckbox,
        InputDecimal,
    },
    props: {
        active: Boolean,
        supplierId: [Number, Boolean],
    },
    data() {
        return {
            templates: window.app.templateType,
            detailsOpen: false,
            useCustomEconomicId: false,
            form: {
                name: '',
                address: '',
                zipcode: '',
                city: '',
                country_id: null,
                email: '',
                phone: '',
                supplier_group_number: null,
                payment_terms_number: null,
                economic_id: '',
                vat_zone_number: null,
                currency: 'DKK',
            },
            error: [],
            economic: {
                vatZones: [],
                paymentTerms: [],
                supplierGroups: [],
            },
        }
    },
    computed: {
        ...mapGetters([
            'listCountries',
            'findCountry'
        ]),
        countryByFormId() {
            return this.findCountry(this.form.country_id)
        },
        countryId: {
            get() {
                return this.countryByFormId;
            },
            set(value) {
                this.form.country_id = value ? value.id : null
            }
        },
        isEdit() {
            return this.supplierId !== null
        },
        saveButtonText() {
            return this.isEdit ? 'Gem' : 'Opret'
        },
        economicIsEnabled() {
            const setting = this.$store.getters.settingByKey('integration.economic.enabled') || {}
            return setting.value === 1
        },
        economicDefaults() {
            let settings = {}
            const keys = ['default_payment_term', 'default_vat_zone']
            keys.forEach(key => {
                const setting = this.$store.getters.settingByKey('integration.economic.' + key)
                settings[key] = setting ? setting.value : 0
            })
            return settings
        },
        currencies() {
            return [
                { value: 'DKK', title: 'DKK' },
                { value: 'EUR', title: 'EUR' },
            ]
        }
    },
    methods: {
        title() {
            return this.isEdit
                ? 'Rediger leverandør'
                : 'Opret leverandør'
        },

        submit() {
            return this.isEdit ? this.update() : this.create()
        },

        create() {
            window.axios.post('/api/suppliers', this.form)
                .then(({data}) => {
                    this.$emit('closed')
                    this.$emit('created', data)
                    this.$store.dispatch('addSupplier', data)
                    this.$store.dispatch('setSuccess', 'Kunden blev oprettet')
                    this.reset()
                })
                .catch(error => {
                    if (error.response && error.response.status === 422) {
                        this.error = error.response.data.errors
                        this.detailsOpen = true
                    }
                })
        },

        update() {
            window.axios.put('/api/suppliers/' + this.supplierId, this.form)
                .then(({data}) => {
                    this.$emit('closed')
                    this.$store.dispatch('updateSupplier', data)
                    this.$store.dispatch('setSuccess', 'Kunden blev opdateret')
                    this.reset()
                })
                .catch(error => {
                    if (error.response && error.response.status === 422) {
                        this.error = error.response.data.errors
                        this.detailsOpen = true
                    }
                })
        },

        reset() {
            if (this.isEdit) {
                const supplier = this.$store.getters.findSupplier(this.supplierId)

                this.form = {
                    name: supplier.name,
                    address: supplier.address,
                    zipcode: supplier.zipcode,
                    city: supplier.city,
                    country_id: supplier.country_id,
                    email: supplier.email,
                    phone: supplier.phone,
                    supplier_group_number: supplier.supplier_group_number,
                    payment_terms_number: supplier.payment_terms_number,
                    economic_id: supplier.economic_id,
                    vat_zone_number: supplier.vat_zone_number,
                    currency: supplier.currency,
                }
            } else {
                this.form = {
                    name: '',
                    address: '',
                    zipcode: '',
                    city: '',
                    country_id: null,
                    email: '',
                    phone: '',
                    supplier_group_number: null,
                    economic_id: '',
                    payment_terms_number: null,
                    vat_zone_number: null,
                    currency: 'DKK',
                }
            }
            this.useCustomEconomicId = false;
            this.error = []
        },

        remove() {
            if (!confirm('Er du sikker på du vil slette ' + this.form.name + '?')) {
                return
            }

            window.axios.delete('/api/suppliers/' + this.supplierId)
                .then(() => {
                    this.$emit('closed')
                    this.$store.dispatch('removeSupplier', this.supplierId)
                    this.$store.dispatch('setSuccess', 'Kunden blev slettet')
                })
                .catch(error => {
                    this.$store.dispatch('setError', 'Kunden kunne ikke slettes')
                })
        },

        async loadEconomicData() {
            const [supplierGroups, vatZones, paymentTerms] = await Promise.all([
                axios.get('/api/integrations/economic/supplier-groups'),
                axios.get('/api/integrations/economic/vat-zones'),
                axios.get('/api/integrations/economic/payment-terms'),
            ])

            this.economic.supplierGroups = supplierGroups.data
            this.economic.vatZones = vatZones.data
            this.economic.paymentTerms = paymentTerms.data

            this.form.payment_terms_number = this.economicDefaults.default_payment_term;
            this.form.vat_zone_number = this.economicDefaults.default_vat_zone;
        },
    },
    created() {
        this.reset()
        this.$store.dispatch('loadSettings')
        this.$store.dispatch('loadCountries', () => {
            const dk = this.listCountries.find(country => country.code === 'DK');
            this.form.country_id = dk ? dk.id : null
        });
        this.loadEconomicData()
    },
    watch: {
        'form.phone'(newValue) {
            if (!this.useCustomEconomicId) {
                let economicId = newValue;
                const charCount = ['dk'].indexOf(this.countryByFormId.code.toLowerCase()) > -1 ? 8 : 9
                if (economicId.length < charCount) {
                    economicId = economicId.padEnd(charCount, '0')
                }
                this.form.economic_id = economicId.substring(economicId.length - charCount)
            }
        }
    },
}
</script>

<style lang="scss" scoped>
.footer-spacer {
    width: 100%;
    display: flex;
    flex-direction:row-reverse;
    justify-content: space-between;
}

.customer-details {
    h4 {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #bbb;
        margin-bottom: 25px;
        cursor: pointer;

        i {
            transition: transform .3s ease-in-out;
        }
    }

    .column {
        display: none;
    }

    &.open {
        h4 i {
            transform: rotate(.5turn);
        }

        .column {
            display: block;
        }
    }


}

.column {
    padding: 0 0.75rem;
}
</style>
